import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { format, parseISO } from 'date-fns';
import { isEmpty, isArray } from 'lodash';
import qs from 'querystring';
import axios from 'axios';

import { Table } from '~/components';
import { RadioGroup, Input } from '~/components/Form';
import { t, number } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';
import MinimizableTable from '../MinimizableTable';

export default function RecordDetails({
  data,
  query,
  sending,
  visible,
  toggleDisabled,
}) {
  const [requestData, setRequestData] = useState([]);
  const [requestTank, serRequestTank] = useState([]);

  const { token } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.user);

  const formRef = useRef(null);

  const beforeCellsMinimizable = useMemo(
    () => [
      { label: 'Volume', cols: 7 },
      { label: 'Amostra', cols: 4 },
    ],
    []
  );

  const cellsMinimizable = useMemo(
    () => [
      { id: 'dean_codi_comp', label: 'Comp' },
      { id: 'dean_cap_comp', label: 'Capac' },
      { id: 'dean_complemento', label: 'Comp' },
      { id: 'dean_vol_amb', label: 'Vol A' },
      { id: 'dean_temp_comp', label: 'Temp' },
      { id: 'dean_vol_med_20_veic', label: 'Vol 20' },
      { id: 'dean_dens_med_amst', label: 'Desn Amb' },
      { id: 'dean_temp_med_amst', label: 'Temp' },
      { id: 'dean_dens_20_amst', label: 'Dens 20' },
      { id: 'amostra_dif', label: 'Dif Dens 20' },
    ],
    []
  );

  const optionsTypeRadio = useMemo(
    () => [
      { label: 'PDF', value: 'pdf' },
      { label: 'Excel', value: 'xls' },
    ],
    []
  );

  const cellsTank = useMemo(
    () => [
      {
        id: 'tade_compartimento',
        label: 'Compartimento',
        align: 'center',
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_datahora_ini',
        label: 'Data Inicio',
        align: 'center',
        format: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm:ss'),
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_datahora_fim',
        label: 'Data Fim',
        align: 'center',
        format: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm:ss'),
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tafi_cod_tafi',
        label: 'Tanque',
        align: 'center',
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_vol_amb_ini',
        label: 'Volume Inicial',
        align: 'center',
        format: (value) => number(value, 2),
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_vol_amb_fim',
        label: 'Volume Fim',
        align: 'center',
        format: (value) => number(value, 2),
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_altura_level01_ini',
        label: 'Altura Inicial (cm)',
        align: 'center',
        format: (value) => number(value, 2),
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_altura_level01_fim',
        label: 'Altura Final (cm)',
        align: 'center',
        format: (value) => number(value, 2),
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_vol_abastecimento',
        label: 'Vol. Abastecido',
        align: 'center',
        format: (value) => number(value, 2),
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'recebido_sonda',
        label: 'Volume Sonda',
        align: 'center',
        format: (value) => number(value, 2),
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_vol_skid',
        label: 'Volume SKID',
        align: 'center',
        format: (value) => number(value, 2),
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_modo_ini',
        label: 'Modo Inicial',
        align: 'center',
        style: () => ({ whiteSpace: 'nowrap' }),
      },
      {
        id: 'tade_modo_fim',
        label: 'Modo Final',
        align: 'center',
        style: () => ({ whiteSpace: 'nowrap' }),
      },
    ],
    []
  );

  const handleGetAPI = useCallback(async () => {
    try {
      toggleDisabled(true);

      const response = await api.get('/relatorio/descargadetalhe', {
        params: {
          wherefield: ['post_cod_posto', 'desc_cod_desc', 'desc_dens_20_doc'],
          valuewherefield: [
            data.post_cod_posto,
            data.desc_cod_desc,
            data.desc_dens_20_doc,
          ],
          typewherefield: ['EQUALS', 'EQUALS', 'EQUALS'],
        },
        paramsSerializer: (serializer) => qs.stringify(serializer),
      });

      const { items } = response.data;

      const createObject = [
        {
          dean_codi_comp: 'Total',
          dean_cap_comp: number(
            items.reduce((total, value) => total + value.dean_cap_comp, 0),
            1
          ),
          dean_complemento: number(
            items.reduce((total, value) => total + value.dean_complemento, 0),
            1
          ),
          dean_vol_amb: number(
            items.reduce((total, value) => total + value.dean_vol_amb, 0),
            1
          ),
          dean_temp_comp: number(
            items.reduce((total, value) => total + value.dean_temp_comp, 0),
            1
          ),
          dean_vol_med_20_veic: number(
            items.reduce(
              (total, value) => total + value.dean_vol_med_20_veic,
              0
            ),
            1
          ),
          repeat: items.map((item) => ({
            ...item,
            dean_cap_comp: number(item.dean_cap_comp, 1),
            dean_complemento: number(item.dean_complemento, 1),
            dean_vol_amb: number(item.dean_vol_amb, 1),
            dean_temp_comp: number(item.dean_temp_comp, 1),
            dean_vol_med_20_veic: number(item.dean_vol_med_20_veic, 1),
            dean_dens_med_amst: number(item.dean_dens_med_amst, 4),
            dean_temp_med_amst: number(item.dean_temp_med_amst, 1),
            dean_dens_20_amst: number(item.dean_dens_20_amst, 4),
            amostra_dif: number(item.amostra_dif, 4),
          })),
        },
      ];

      setRequestData(createObject);
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      toggleDisabled(false);
    }
  }, [data]);

  const handleGetRequestTank = useCallback(async () => {
    try {
      toggleDisabled(true);

      const response = await api.get('/relatorio/tanquesdescarga', {
        params: {
          wherefield: ['post_cod_posto', 'dead_cod_descarga'],
          valuewherefield: [data.post_cod_posto, data.desc_cod_desc],
          typewherefield: ['EQUALS', 'EQUALS'],
          pagesize: 99999,
        },
        paramsSerializer: (serializer) => qs.stringify(serializer),
      });

      serRequestTank(response.data.items);
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      toggleDisabled(false);
    }
  }, [data]);

  const handleSubmit = useCallback(
    async (formData) => {
      try {
        toggleDisabled(true);

        const REACT_APP_URL_DOWNLOAD = sessionStorage.getItem('URL_REPORT');
        const dateSplit = query.period.split(' até ');
        const dataFilter = {
          relatorio: 'DESCARGASDETALHE2',
          token,
          usuario: profile.username,
          strusuario: profile.name,
          datainicial: dateSplit[0],
          datafinal: dateSplit[1],
          codposto: data.post_cod_posto,
          strposto: data.post_desc,
          noctvt: data.desc_num_doc,
          tiporel: '',
          ope: data.oper_cod_oper,
          pedido: data.desc_num_pedido,
          tipodoc: data.desc_tipo_doc,
          tqf: data.tafi_cod_tafi,
          data: format(parseISO(data.desc_datahora), 'dd/MM/yyyy'),
          turno: data.turn_cod_turn,
          hora: format(parseISO(data.desc_datahora), 'HH:mm:ss'),
          volamb: data.desc_vol_temp_car_doc,
          temp: data.desc_temp_car_doc,
          dens20: data.desc_dens_20_doc,
          vol20: data.desc_vol_20_doc,
          codDescarga: data.desc_cod_desc,
          obs: '',
          ...JSON.parse(JSON.stringify(formData)),
        };

        const url = new URL(REACT_APP_URL_DOWNLOAD);
        url.search = new URLSearchParams(dataFilter);

        const response = await axios({
          url: url.href,
          method: 'POST',
          responseType: 'blob',
        });

        const file = new Blob([response.data], { type: response.data.type });

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(file);
        } else {
          const objectUrl = URL.createObjectURL(file);
          window.open(objectUrl);
        }
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      } finally {
        toggleDisabled(false);
      }
    },
    [token, profile, query, data]
  );

  useEffect(() => {
    if (!isEmpty(data)) {
      handleGetAPI();
      handleGetRequestTank();
    }
  }, [data]);

  useEffect(() => {
    if (!visible) {
      setRequestData([]);
      serRequestTank([]);
    }
  }, [visible]);

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  if (isEmpty(query)) return <div />;

  return (
    <S.Wrapper
      ref={formRef}
      initialData={{ tipo: 'pdf' }}
      onSubmit={handleSubmit}
    >
      <ul>
        <li>
          <span>Data Emissão:</span>
          {query.issuance_date}
        </li>
        <li>
          <span>Período:</span>
          {query.period}
        </li>
        <li>
          <span>Emissor:</span>
          {query.emitter}
        </li>
        <li>
          <span>Posto:</span>
          {data.post_desc}
        </li>
        <li>
          <span>Tipo:</span>
          {query.type}
        </li>
      </ul>

      <S.WrapperOL columns={5}>
        <li>
          <span>TFQ:</span>
          {data.tafi_code_tafi}
        </li>
        <li>
          <span>Data:</span>
          {format(parseISO(data.desc_datahora), 'dd/MM/yyyy')}
        </li>
        <li>
          <span>Turno:</span> {data.turn_num_turn}
        </li>
        <li>
          <span>Hora:</span>
          {format(parseISO(data.desc_datahora), 'HH:mm:ss')}
        </li>
        <li>
          <span>Operador:</span>
          {data.oper_cod_oper}
        </li>
      </S.WrapperOL>

      <S.WrapperHeading>Dados da Nota Fiscal</S.WrapperHeading>

      <S.WrapperOL columns={6}>
        <li>
          <span>Pedido:</span>
          {data.desc_num_pedido}
        </li>
        <li>
          <span>Nº CT/VT:</span>
          {data.desc_veic_doc}
        </li>
        <li>
          <span>Vol. Amb:</span>
          {number(data.desc_vol_temp_car_doc, 2)}
        </li>
        <li>
          <span>Temp:</span>
          {number(data.desc_temp_car_doc, 1)}
        </li>
        <li>
          <span>Dens. 20:</span>
          {data.desc_dens_20_doc}
        </li>
        <li>
          <span>Vol. 20:</span>
          {number(data.desc_vol_20_doc, 1)}
        </li>
      </S.WrapperOL>

      <S.WrapperHeading>Medições CT</S.WrapperHeading>

      <MinimizableTable
        beforeCells={beforeCellsMinimizable}
        cells={cellsMinimizable}
        data={requestData}
      />

      <S.WrapperHeading>Medições Tanque por Compartimento</S.WrapperHeading>

      <Table cells={cellsTank} data={requestTank} size="small" />

      <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
        <RadioGroup
          row
          name="tipo"
          label="Formato"
          options={optionsTypeRadio}
        />
      </div>

      <Input multiline name="obs" label="Observação" rows={4} />
    </S.Wrapper>
  );
}

RecordDetails.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  query: PropTypes.objectOf(PropTypes.any).isRequired,
  sending: PropTypes.bool.isRequired,
  visible: PropTypes.bool.isRequired,
  toggleDisabled: PropTypes.func.isRequired,
};
